import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ArrowRight, BarChart3 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { auditTopActionsQueryOptions } from '@/lib/audit';

/**
 * Top-8 action codes in the last 24h. Pairs with SecurityStatsCard
 * (which shows the aggregate "system pulse") — this card breaks the
 * pulse down into its constituent actions so unusual bursts pop out
 * without scrolling the full /audit list.
 *
 * Each row is clickable — drills to /audit?action=<code> so admins
 * can immediately see the offending events. The bar visualization
 * is normalized to the top entry so the relative scale reads even
 * when one action dominates the window.
 */
export function TopActionsCard() {
  const q = useQuery(auditTopActionsQueryOptions());
  const items = q.data?.items ?? [];
  const max = items[0]?.count ?? 0;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <BarChart3 className="h-4 w-4 text-slate-400" />
          ที่เกิดบ่อย 24 ชม.
        </CardTitle>
        <Link
          to="/audit"
          className="flex items-center text-xs text-blue-600 hover:underline dark:text-blue-400"
        >
          ดู audit <ArrowRight className="ml-0.5 h-3 w-3" />
        </Link>
      </CardHeader>
      <CardContent>
        {q.isLoading ? (
          <p className="text-sm text-slate-500">กำลังโหลด...</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-slate-400">ยังไม่มีเหตุการณ์ในช่วงนี้</p>
        ) : (
          <ul className="space-y-1.5">
            {items.map((it) => {
              // Bar width = ratio against the top entry; min 6% so the
              // shortest non-zero row is still visible.
              const pct = max > 0 ? Math.max(6, Math.round((it.count / max) * 100)) : 0;
              return (
                <li key={it.action}>
                  <Link
                    to="/audit"
                    search={{ action: it.action }}
                    className="group flex items-center gap-2 text-xs"
                    title={`กรอง audit ตาม ${it.action}`}
                  >
                    <span className="w-32 shrink-0 truncate font-mono text-slate-700 group-hover:text-slate-900 dark:text-slate-300 dark:group-hover:text-slate-100">
                      {it.action}
                    </span>
                    <div className="relative h-3 flex-1 overflow-hidden rounded bg-slate-100 dark:bg-slate-800">
                      <div
                        className="absolute inset-y-0 left-0 bg-slate-400 group-hover:bg-slate-500 dark:bg-slate-600 dark:group-hover:bg-slate-500"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className="w-10 shrink-0 text-right tabular-nums text-slate-500">
                      {it.count.toLocaleString('th-TH')}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
